import * as React from 'react';
import classnames from 'classnames';
import Res from 'core/api/Res';
import { Currency } from '../../utils';
import { Constraint, check } from './Constraints';
import { applyColumnFormat, DataGridColumn } from './DataGrid.Column';
import { Binding } from 'core/infrastructure';
import KeyCode from '../KeyCode';
import { PropertyType } from 'core/infrastructure/Binding';
import Button from './Button';


import { Cell } from 'fixed-data-table-2';

export type TextCellConstraint<T> = (value: string, item: T) => string;

interface TextCellErrorContent {
	errorMsg: string;
	value: string;
	itemKey: any;
}

export function createTextCellKey(itemKey: any, rowIndex: number, columnIndex: number) {
	return `${itemKey}_${rowIndex}_${columnIndex}`;
}

export function createTextInputId(rowIndex: number, columnIndex: number) {
	return `${rowIndex}${columnIndex}`;
}

export type TextCellErrors = { [cellKey: string]: TextCellErrorContent };

interface Props<T> extends React.HTMLProps<TextCell<T>> {
	item: T;
	itemKey?: (item: T) => any;
	initialContent: TextCellErrorContent;
	propertyPath: string;
	cellErrors?: TextCellErrors;
	rules?: Constraint | Constraint[];
	rowIndex: number;
	columnIndex: number;
	onTab?: (rowIndex: number, columnIndex: number) => void;
	onInverseTab?: (rowIndex: number, columnIndex: number) => void;
	onArrowUp?: (rowIndex: number, columnIndex: number) => void;
	onArrowDown?: (rowIndex: number, columnIndex: number) => void;
	onArrowLeft?: (rowIndex: number, columnIndex: number) => void;
	onArrowRight?: (rowIndex: number, columnIndex: number) => void;
	onGetFocus?: (rowIndex: number, columnIndex: number) => void;
	propertyType: PropertyType;
}

interface State {
	value: string;
	errorMsg?: string;
	oldInitialContent: TextCellErrorContent;
}

class ErrorInfoPopover extends React.Component<{ msg: string }, EmptyState> {

	render() {
		const onClick = () => {
			// Es feo, pero por lo menos funciona, no como el popover que había antes
			alert(this.props.msg);
		};

		return <span className='input-group-btn'>
			<Button onClick={onClick} title={this.props.msg}><i className='text-error fa fa-exclamation'></i></Button>
		</span>;
	}
}

export class TextCell<T> extends React.Component<Props<T>, State> {

	private input = React.createRef<HTMLInputElement>();

	constructor(props: Props<T>) {
		super(props);

		this.state = {
			value: props.initialContent.value,
			errorMsg: props.initialContent.errorMsg,
			// Necesito guardarlo en el estado para resetear el estado actual
			// cuando cambien los props
			oldInitialContent: props.initialContent
		};

		this.onBlur = this.onBlur.bind(this);
	}

	static getDerivedStateFromProps<T>(nextProps: Props<T>, state: State) {

		if (nextProps.initialContent.value !== state.oldInitialContent.value ||
			nextProps.initialContent.errorMsg !== state.oldInitialContent.errorMsg)
			return ({
				value: nextProps.initialContent.value,
				errorMsg: nextProps.initialContent.errorMsg,
				oldInitialContent: nextProps.initialContent
			});

		return null;
	}

	getItem(): T {
		return this.props.item;
	}

	validate() {

		this.registerError('', '');

		const value = this.state.value;

		if (this.props.required && (!value || !value.trim())) {
			this.registerError(Res.Msg.MSG_VALUE_REQUIRED(), value);
			return false;
		}

		const result = Binding.parse(this.state.value, this.props.propertyType, !!this.props.required);

		if (result.errorMsg) {
			this.registerError(result.errorMsg, value);
			return false;
		}

		const rulesError = this.props.rules && check(this.props.rules, result.value);

		if (rulesError) {
			this.registerError(rulesError, value);
			return false;
		}

		return true;
	}

	updateItem() {
		const result = Binding.parse(this.state.value, this.props.propertyType, !!this.props.required);
		Binding.setValue(this.props.item, this.props.propertyPath, result.value);
	}

	private onBlur() {
		if (this.validate())
			this.updateItem();
	}

	private registerError(errorMsg: string, value: string) {

		const cellsErrors = this.props.cellErrors || {};

		const itemKey = this.props.itemKey ? this.props.itemKey(this.props.item) : '';
		const cellKey = createTextCellKey(itemKey, this.props.rowIndex, this.props.columnIndex);

		if (errorMsg.length === 0 && cellsErrors[cellKey]) {
			delete cellsErrors[cellKey];
		}

		if (errorMsg.length)
			cellsErrors[cellKey] = { errorMsg, value, itemKey };

		this.setState({ errorMsg });
	}

	protected createErrorPopover(errorMsg: string) {
		return <ErrorInfoPopover msg={errorMsg} />;
	}

	private activate() {
		const inputNode = this.input.current!;
		inputNode.focus();
		inputNode.select();
	}

	render() {

		const onChange = (e: any) => {
			const value = e.target.value;
			this.setState({ value: value });
		};

		const onKeyDown = (e: React.KeyboardEvent<any>) => {

			const processKey = (e: React.KeyboardEvent<any>, process?: (rowIndex: number, columnIndex: number) => void) => {

				e.preventDefault();
				e.stopPropagation();

				this.onBlur();

				if (process)
					process(this.props.rowIndex, this.props.columnIndex);
			};

			if (e.keyCode === KeyCode.KEY_TAB && e.shiftKey)
				processKey(e, this.props.onInverseTab);
			else if (e.keyCode === KeyCode.KEY_TAB)
				processKey(e, this.props.onTab);
			else if (e.keyCode === KeyCode.KEY_RETURN)
				processKey(e, this.props.onArrowDown);
			else if (e.keyCode === KeyCode.KEY_DOWN)
				processKey(e, this.props.onArrowDown);
			else if (e.keyCode === KeyCode.KEY_UP)
				processKey(e, this.props.onArrowUp);
			else if (e.keyCode === KeyCode.KEY_RIGHT) {
				if (this.state.value.length === e.currentTarget.selectionEnd)
					processKey(e, this.props.onArrowRight);
			}
			else if (e.keyCode === KeyCode.KEY_LEFT) {
				if (e.currentTarget.selectionStart === 0)
					processKey(e, this.props.onArrowLeft);
			}
		};

		const onFocus = () => {
			this.activate();
			this.props.onGetFocus?.(this.props.rowIndex, this.props.columnIndex);
		};

		const isNumericContent = ['int', 'decimal'].includes(this.props.propertyType);

		const onInput = (evt: React.KeyboardEvent<HTMLInputElement>) => {

			const start = evt.currentTarget.selectionStart;
			const end = evt.currentTarget.selectionEnd;

			const replaced = isNumericContent
				? evt.currentTarget.value.replace('.', Currency.DECIMAL_SEPARATOR)
				: evt.currentTarget.value;

			if (replaced !== evt.currentTarget.value) {
				evt.currentTarget.value = replaced;
				evt.currentTarget.selectionStart = start;
				evt.currentTarget.selectionEnd = end;
			}
		};

		const inputId = `${this.props.rowIndex}${this.props.columnIndex}`;
		const type = 'text';
		const input = <input id={inputId} type={type} step='any' onKeyDown={onKeyDown} ref={this.input} className='form-control' value={this.state.value} onChange={onChange} onBlur={this.onBlur} onFocus={onFocus} onInput={onInput} />;

		const inputGroup = this.state.errorMsg ?
			<div className='input-group'>
				{input}
				{this.createErrorPopover(this.state.errorMsg)}
			</div> : input;

		return <Cell className={isNumericContent ? 'number' : 'text'}>
			<div className={classnames({ 'form-group': true, 'has-error': !!this.state.errorMsg })}>
				{inputGroup}
			</div>
		</Cell>;
	}
}

type TextCellCtor<T> = new (props: Props<T>) => TextCell<T>;

export interface CreateTextCellParams<T> {
	column: DataGridColumn<T>;
	items: T[];
	columnIndex: number;
	onTab: (rowIndex: number, columnIndex: number) => void;
	onInverseTab: (rowIndex: number, columnIndex: number) => void;
	onArrowDown: (rowIndex: number, columnIndex: number) => void;
	onArrowUp: (rowIndex: number, columnIndex: number) => void;
	onArrowLeft: (rowIndex: number, columnIndex: number) => void;
	onArrowRight: (rowIndex: number, columnIndex: number) => void;
	onGetFocus: (rowIndex: number, columnIndex: number) => void;
	cellsErrors: { [cellKey: string]: TextCellErrorContent };
	itemKey: (item: T) => any;
	propertyType: PropertyType;
}

export function createTextCell<T>(params: CreateTextCellParams<T>) {

	const buildRules = (cellConstraint: TextCellConstraint<T> | TextCellConstraint<T>[], item: T) => {

		let rules: Constraint[] = [];
		if (typeof cellConstraint === 'function')
			rules.push((value: string) => cellConstraint(value, item));
		else
			rules = cellConstraint.map(f => (value: string) => f(value, item));

		return rules;
	};

	return function TextCellWrapper({ rowIndex }: { rowIndex: number }) {

		const item = params.items[rowIndex];

		const propertyPath = Binding.propertyPathToString(params.column.linkTo);
		const boundValue = Binding.getValue(item, propertyPath);

		const formattedValue = applyColumnFormat(params.column, boundValue, item);
		const rules = params.column.cellConstraint ? buildRules(params.column.cellConstraint, item) : [];

		const Cell: TextCellCtor<T> = TextCell;
		const cellKey: string = createTextCellKey(params.itemKey(item), rowIndex, params.columnIndex);
		const cellContent: TextCellErrorContent = params.cellsErrors[cellKey] || { value: formattedValue, errorMsg: '' };

		return <Cell
			key={cellKey}
			rowIndex={rowIndex}
			columnIndex={params.columnIndex}
			item={item}
			itemKey={params.itemKey}
			initialContent={cellContent}
			rules={rules}
			propertyPath={propertyPath}
			cellErrors={params.cellsErrors}
			onTab={params.onTab}
			onInverseTab={params.onInverseTab}
			onArrowDown={params.onArrowDown}
			onArrowUp={params.onArrowUp}
			onArrowLeft={params.onArrowLeft}
			onArrowRight={params.onArrowRight}
			onGetFocus={params.onGetFocus}
			propertyType={params.propertyType}
		/>;
	};
}
